import { z } from 'zod';

/** Deterministic CRM wizard steps (no LLM discovery). */
export const crmFallbackWizardStepSchema = z.enum([
  'direction',
  'service',
  'staff',
  'slot',
  'confirm',
  'unavailable',
]);
export type CrmFallbackWizardStep = z.infer<typeof crmFallbackWizardStepSchema>;

export const crmFallbackOptionKindSchema = z.enum([
  'direction',
  'service',
  'staff',
  'slot',
  'confirm',
  'nav',
]);
export type CrmFallbackOptionKind = z.infer<typeof crmFallbackOptionKindSchema>;

/**
 * Server-validated CRM wizard option. Only real catalog/availability ids — never LLM guesses.
 * Safe for client rendering; no internal error strings.
 */
export const crmFallbackOptionSchema = z
  .object({
    optionId: z.string().min(1).max(64),
    title: z.string().min(1).max(200),
    kind: crmFallbackOptionKindSchema,
    serviceId: z.string().min(1).max(32).optional(),
    staffExternalId: z.string().min(1).max(64).optional(),
    slotStartAt: z.string().min(1).max(64).optional(),
    priceFrom: z.number().nonnegative().nullable().optional(),
    durationMin: z.number().int().positive().nullable().optional(),
  })
  .strict();
export type CrmFallbackOption = z.infer<typeof crmFallbackOptionSchema>;

export const crmFallbackStatusCodeSchema = z.enum(['ok', 'crm_unavailable']);
export type CrmFallbackStatusCode = z.infer<typeof crmFallbackStatusCodeSchema>;

export const crmFallbackSelectedDirectionSchema = z.enum(['face', 'body', 'massage', 'relax']);
export type CrmFallbackSelectedDirection = z.infer<typeof crmFallbackSelectedDirectionSchema>;

/** Public CRM fallback view — never includes CrmFallbackReason or provider errors. */
export const crmFallbackPublicViewSchema = z
  .object({
    step: crmFallbackWizardStepSchema,
    statusCode: crmFallbackStatusCodeSchema.default('ok'),
    options: z.array(crmFallbackOptionSchema).max(40),
    selectedDirection: crmFallbackSelectedDirectionSchema.optional(),
    selectedServiceId: z.string().min(1).max(32).optional(),
    selectedStaffExternalId: z.string().min(1).max(64).optional(),
    selectedSlotStartAt: z.string().min(1).max(64).optional(),
  })
  .strict();
export type CrmFallbackPublicView = z.infer<typeof crmFallbackPublicViewSchema>;
